/**
 * Geocodificador especializado para puestos de socorro en playas andaluzas
 *
 * Conecta con DERA G12 (Servicios) para acceder a la capa:
 * - `g12_37_PuestosSocorro` — 533 features Andalucía (verif. empírica
 *   2026-05-19, sample n=500 + GetCapabilities). Cobertura litoral
 *   andaluz: módulos de socorrismo en playas operados típicamente por
 *   Cruz Roja y administraciones locales con campo `sanitarios` que
 *   indica presencia de personal sanitario (None=484, 1=15, 2=1 sobre
 *   n=500 muestra).
 *
 * Contexto institucional:
 * - Decreto 197/2024 (PTEL · Sistema Local de Protección Civil)
 * - Planes de Seguridad y Salvamento en Playas (municipios litorales)
 *
 * Fuente oficial:
 * - WFS DERA G12: https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * - Layer:        DERA_g12_servicios:g12_37_PuestosSocorro
 *
 * Schema empírico (sample n=500 GetFeature application/json, 2026-05-19,
 * persistido en `.ptel/p0_wave2_20260519/sample_g12_37_n500.json`):
 *
 * | Campo       | Tipo (sample)  | Nillable | Cardinalidad |
 * |:------------|:---------------|:---------|:-------------|
 * | id_dera     | number         | NO       | 1..1         |
 * | nombre      | string         | YES      | 0..1         |
 * | playa       | string         | YES      | 0..1 (213 vals únicos n=500) |
 * | sanitarios  | number\|null   | YES      | 0..1 (96,8 % null) |
 * | cod_mun     | string         | YES      | 0..1 (INE5)  |
 * | municipio   | string         | YES      | 0..1         |
 * | provincia   | string         | YES      | 0..1         |
 * | geom        | MultiPoint     | YES      | 0..1 (card.1)|
 *
 * NOTA HX-CAPTURA-MAXIMAL (rumbo 2 · 2.ª aplicación productiva 2026-05-19):
 * - `parseFeature` preserva la totalidad del schema fuente en `properties`,
 *   no un denominador común. Esto garantiza que la BBDD enriquecida derivada
 *   del pipeline conserve los campos específicos de g12_37 (`nombre`,
 *   `playa`, `sanitarios`, `id_dera`) aunque el flujo activo de geocoding
 *   no los necesite todos.
 * - Se añaden 4 campos de provenance (`source_layer`, `source_endpoint`,
 *   `schema_version`, `fetched_at`) que permiten re-validar o refrescar
 *   la BBDD sin re-consultar metadata externa.
 *
 * Tipología (DOP-WAVE2-7=α, ratificada empíricamente 2026-05-19): se usa
 * la macro `TipologiaPTEL.EMERGENCIAS` por coherencia cluster F2 SEG
 * ampliado oleada-2 (alineada con WAVE-1 maremotos g12_38) y mapeo
 * canónico `TIPOLOGIA_KEYWORDS` del proyecto (`cruz roja` ∈ EMERGENCIAS,
 * `toProcessedRecord.ts` L46). El detalle fino (`PUESTO_SOCORRO`) se
 * persiste como atributo libre, no como nueva categoría macro (TUN1:
 * «No Decreto fixes closed taxonomy»).
 *
 * @module services/geocoding/specialized
 * @since  2026-05-19 (WAVE-2 socorro · rumbo 2)
 */

import type {
  WFSFeature,
  WFSSearchOptions,
} from './WFSBaseGeocoder';
import { WFSBaseGeocoder } from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig } from '../../../types/infrastructure';

/**
 * Opciones de búsqueda específicas para puestos de socorro.
 *
 * Extiende `WFSSearchOptions` con `playa` para filtrar por nombre de
 * playa concreto (campo `playa` de la fuente tiene 213 valores únicos
 * en el sample n=500 → alto poder discriminante para queries finas).
 */
export interface PuestoSocorroSearchOptions extends WFSSearchOptions {
  /** Nombre de playa (`playa`); NULL-permisivo en la fuente. */
  playa?: string;
}

/**
 * Identificador semántico granular del dataset (no es una `TipologiaPTEL`).
 *
 * Se persiste en `properties.tipoOriginal` para que la BBDD enriquecida
 * preserve la naturaleza específica del punto sin contaminar la taxonomía
 * macro cerrada de 19 categorías (TUN1).
 */
export const TIPO_ORIGINAL_PUESTO_SOCORRO = 'PUESTO_SOCORRO' as const;

/**
 * Versión del schema empírico verificada. Incrementar si el
 * `DescribeFeatureType` upstream cambia (nuevos campos, nuevas
 * cardinalidades, cambio de tipo).
 */
export const SCHEMA_VERSION_G12_37 = 'g12_37_v20260519' as const;

/**
 * Geocodificador especializado para puestos de socorro en playas andaluzas.
 *
 * Precisión esperada: ±5-10 m (coordenadas oficiales DERA G12 publicadas
 * en EPSG:25830 nativo, sin reproyección intermedia).
 *
 * Cobertura: 533 features distribuidos en municipios litorales andaluces.
 * Ámbito de aplicación: Planes de Seguridad y Salvamento en Playas de los
 * municipios del Sistema Local de Protección Civil (Decreto 197/2024).
 *
 * Independiente de `WFSMaremotosGeocoder` (cluster F2 SEG ampliado
 * oleada-2) y de `WFSSecurityGeocoder` por decisión arquitectónica
 * `DOP-WAVE1-1` 18-may: cada capa DERA G12 tiene su propio geocoder
 * dedicado para preservar HX-CAPTURA-MAXIMAL pleno (schemas
 * heterogéneos cross-capa: 5 campos comunes + 2 específicos g12_37
 * `playa`+`sanitarios` perderían bajo anti-patrón denominador común).
 *
 * @example
 * ```typescript
 * const geocoder = new WFSPuestoSocorroGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Módulo de socorrismo',
 *   municipality: 'El Ejido',
 *   codMun: '04902',
 *   playa: 'De Guardias Viejas',
 * });
 * // result.confidence >= 80 (match oficial DERA)
 * // result.properties.tipoOriginal === 'PUESTO_SOCORRO'
 * // result.properties.tipologiaPTEL === 'EMERGENCIAS'
 * // result.properties.contextoEmergencia === 'SOCORRO_LITORAL'
 * ```
 */
export class WFSPuestoSocorroGeocoder extends WFSBaseGeocoder {
  /** Endpoint unificado DERA G12 Servicios. */
  public static readonly DERA_G12_ENDPOINT =
    'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs';

  /** Capa única consumida por este geocoder. */
  public static readonly LAYER_PUESTOS_SOCORRO =
    'g12_37_PuestosSocorro';

  /** Identificador completo de la fuente para `properties.source_layer`. */
  public static readonly SOURCE_LAYER_ID =
    'DERA_g12_servicios:g12_37_PuestosSocorro';

  /**
   * Configuración por defecto.
   *
   * - `outputSRS = EPSG:25830` — fuente nativa, sin reproyección.
   * - `fuzzyThreshold = 0.40` — más estricto que Maremotos (`0.45`)
   *   porque g12_37 tiene campo `nombre` propio (texto descriptivo
   *   del puesto: «Módulo de socorrismo», «Puesto Cruz Roja», etc.)
   *   más discriminante que el match contra `localidad`+`municipio`
   *   exclusivo de g12_38 maremotos.
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: WFSPuestoSocorroGeocoder.DERA_G12_ENDPOINT,
      layerName: WFSPuestoSocorroGeocoder.LAYER_PUESTOS_SOCORRO,
      fuzzyThreshold: 0.40,
      timeout: 15000,
      outputSRS: 'EPSG:25830',
    };
  }

  /**
   * Parsea un feature GeoJSON aplicando HX-CAPTURA-MAXIMAL.
   *
   * Mantiene:
   * 1. Atributos fuente íntegros (`...props`).
   * 2. Campos provenance (capa, endpoint, schema, timestamp).
   * 3. Campos semánticos PTEL (tipología macro + tipo original + contexto).
   *
   * `MultiPoint` cardinalidad=1 verificada empíricamente en sample n=500
   * (2026-05-19): `parseGeometry` de la base normaliza a `[x, y]` y se
   * registra `geom_type` + `geom_pointCount` para preservar fidelidad
   * al schema fuente.
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      const pt = this.parseGeometry(
        geom,
        `PuestoSocorro feature ${String(feature.id ?? 'sin-id')}`,
      );
      if (!pt) {
        console.warn(
          'Feature PuestoSocorro sin geometría válida:',
          feature.id,
        );
        return null;
      }

      const [x, y] = pt;

      // Validación rango UTM30 Andalucía
      // (skill ptel-geocoding: X∈[100000,900000] Y∈[3900000,4400000])
      if (x < 100000 || x > 900000 || y < 3900000 || y > 4400000) {
        console.warn(
          'PuestoSocorro: coordenadas fuera de rango Andalucía:',
          x,
          y,
        );
        return null;
      }

      // Cálculo cardinalidad MultiPoint (preserva fidelidad al schema
      // fuente; sample n=500 confirma cardinalidad=1 en el 100 %).
      const geomType = geom?.type ?? 'unknown';
      const geomPointCount =
        geom && geom.type === 'MultiPoint' && Array.isArray(geom.coordinates)
          ? geom.coordinates.length
          : 1;

      // Nombre canónico para el motor de fuzzy-matching:
      // nombre nativo → playa → municipio → fallback descriptivo.
      // g12_37 a diferencia de g12_38 maremotos SÍ expone `nombre`
      // del puesto (texto descriptivo: «Módulo de socorrismo»,
      // «Puesto Cruz Roja», etc.) → preferencia 1.ª.
      const nombre = getStringProp(props, 'nombre');
      const playa = getStringProp(props, 'playa');
      const municipio = getStringProp(props, 'municipio');
      const displayName =
        nombre ||
        playa ||
        municipio ||
        `Puesto socorro ${String(props.id_dera ?? feature.id ?? '?')}`;

      return {
        name: displayName,
        x,
        y,
        municipality: municipio,
        province: getStringProp(props, 'provincia'),
        // `address` se mantiene vacío: la fuente no expone dirección postal
        // para g12_37 (a diferencia de g12_36 que sí tiene `direccion`).
        address: '',
        properties: {
          // HX-CAPTURA-MAXIMAL · campos fuente íntegros
          ...props,

          // Campos derivados (geometría)
          geom_type: geomType,
          geom_pointCount: geomPointCount,

          // Campos semánticos PTEL (DOP-WAVE2-7=α · TipologiaPTEL macro cerrada)
          tipologiaPTEL: 'EMERGENCIAS',
          tipoOriginal: TIPO_ORIGINAL_PUESTO_SOCORRO,
          decretoReferencia: '197/2024',
          contextoEmergencia: 'SOCORRO_LITORAL',

          // Campos provenance (3B · HX-CAPTURA-MAXIMAL pleno)
          source_layer: WFSPuestoSocorroGeocoder.SOURCE_LAYER_ID,
          source_endpoint: WFSPuestoSocorroGeocoder.DERA_G12_ENDPOINT,
          schema_version: SCHEMA_VERSION_G12_37,
          fetched_at: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error('Error parseando feature PuestoSocorro:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para `g12_37`.
   *
   * Override completo (no invoca `super`) — el filtro base usa claves
   * mayúsculas legacy que GeoServer DERA G12 rechaza con HTTP 400
   * (case-sensitive en CQL property names, verif. WSSD 15-may).
   *
   * Orden de preferencia:
   * 1. `cod_mun = 'X'` (INE5 exacto) si se aporta `codMun`.
   * 2. `municipio ILIKE '%X%'` (texto libre) si solo hay `municipality`.
   * 3. `provincia ILIKE '%X%'` (filtro provincial adicional).
   * 4. `playa ILIKE '%X%'` (filtro fino específico capa — 213 valores
   *    únicos en sample n=500; el filtro implícitamente excluye NULL).
   * 5. `BBOX(geom, …, 'EPSG:25830')` (filtro espacial).
   */
  protected buildCQLFilter(options: PuestoSocorroSearchOptions): string {
    const filters: string[] = [];

    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    } else if (options.municipality) {
      filters.push(
        `municipio ILIKE '%${this.escapeCQL(options.municipality)}%'`,
      );
    }

    if (options.province) {
      filters.push(
        `provincia ILIKE '%${this.escapeCQL(options.province)}%'`,
      );
    }

    if (options.playa) {
      filters.push(
        `playa ILIKE '%${this.escapeCQL(options.playa)}%'`,
      );
    }

    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(
        `BBOX(geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`,
      );
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }
}
